/**
 * CallAssistant iframe messaging protocol.
 *
 * SYNC NOTICE: Any changes to the messaging types or handlers in this file
 * MUST be synced back to docs/features/mx-call/overview.md
 *
 * All messages use a discriminated union keyed on the `type` field.
 * Messages requiring a response use the MessagePort (MessageChannel API)
 * request/reply pattern. See docs/features/mx-call/overview.md for
 * full protocol documentation and usage examples.
 */
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { useTolgee } from "@tolgee/react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getAppOrigin,
  getDeployEnv,
  getVersion,
  MXCALL_URL,
} from "../../../environment.js";
import { useAuth } from "../../auth/useAuth.js";
import { useUserData } from "../../auth/useUserData.js";
import { getCapacitorAppUrl } from "../../firebase.js";
import { Page } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";

// ---------------------------------------------------------------------------
// Message types — discriminated union by `type`, then by `method` where needed
// ---------------------------------------------------------------------------

/** Fire-and-forget: iframe requests parent navigation */
type NavigationMessage = {
  type: "navigate";
  replace?: boolean;
} & (
  | { path: string }
  | { entity: "project"; id: string }
  | { entity: "crmCompany"; id: string }
  | { entity: "crmPerson"; personId: string; companyId: string }
  | { entity: "quote"; projectId: string; quoteId: string }
  | { entity: "requirement"; projectId: string; requirementId: string }
);

/** Request/reply via MessagePort: iframe requests auth headers */
type AuthMessage = {
  type: "auth";
  method: "requestAuthHeaders";
};

/** Request/reply via MessagePort: iframe requests system/environment info */
type SystemMessage = {
  type: "system";
  method: "getEnvironment" | "getLatestVersion";
};

/** Fire-and-forget: iframe notifies parent of its current route */
type RouteChangeMessage = {
  type: "route-change";
  path: string;
};

/** Union of all messages the MX Call iframe can send to the parent */
type CallAssistantMessage =
  | NavigationMessage
  | AuthMessage
  | SystemMessage
  | RouteChangeMessage;

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

const isNavigationMessage = (data: unknown): data is NavigationMessage => {
  if (
    typeof data !== "object" ||
    data === null ||
    !("type" in data) ||
    data.type !== "navigate"
  ) {
    return false;
  }

  // Check for simple path navigation
  if ("path" in data && typeof data.path === "string") {
    return true;
  }

  // Check for entity-based navigation
  if ("entity" in data && typeof data.entity === "string") {
    const entity = data.entity;
    const id = "id" in data ? data.id : undefined;

    if (entity === "project" && typeof id === "string") {
      return true;
    }
    if (entity === "crmCompany" && typeof id === "string") {
      return true;
    }
    if (
      entity === "crmPerson" &&
      "personId" in data &&
      typeof data.personId === "string" &&
      "companyId" in data &&
      typeof data.companyId === "string"
    ) {
      return true;
    }
    if (
      entity === "quote" &&
      "projectId" in data &&
      typeof data.projectId === "string" &&
      "quoteId" in data &&
      typeof data.quoteId === "string"
    ) {
      return true;
    }
    if (
      entity === "requirement" &&
      "projectId" in data &&
      typeof data.projectId === "string" &&
      "requirementId" in data &&
      typeof data.requirementId === "string"
    ) {
      return true;
    }
  }

  return false;
};

const isAuthMessage = (data: unknown): data is AuthMessage =>
  typeof data === "object" &&
  data !== null &&
  "type" in data &&
  data.type === "auth" &&
  "method" in data &&
  data.method === "requestAuthHeaders";

const isSystemMessage = (data: unknown): data is SystemMessage =>
  typeof data === "object" &&
  data !== null &&
  "type" in data &&
  data.type === "system" &&
  "method" in data &&
  typeof data.method === "string" &&
  (data.method === "getEnvironment" || data.method === "getLatestVersion");

const isRouteChangeMessage = (data: unknown): data is RouteChangeMessage =>
  typeof data === "object" &&
  data !== null &&
  "type" in data &&
  data.type === "route-change" &&
  "path" in data &&
  typeof data.path === "string";

const isCallAssistantMessage = (data: unknown): data is CallAssistantMessage =>
  isNavigationMessage(data) ||
  isAuthMessage(data) ||
  isSystemMessage(data) ||
  isRouteChangeMessage(data);

// ---------------------------------------------------------------------------
// msys_url resolution
// ---------------------------------------------------------------------------

/**
 * The origin handed to the embedded mx-call-dashboard as `msys_url`. Prefers
 * the runtime-config value, which is correct on every platform including
 * native Capacitor builds (where `window.location.hostname` is always
 * "localhost"). Falls back to `window.location.origin` when the config value
 * isn't populated yet for this env.
 */
function resolveMsysUrl(): string {
  if (getDeployEnv() === "local") {
    return "";
  }
  const appOrigin = getAppOrigin();
  if (appOrigin) {
    return appOrigin;
  }
  return window.location.hostname !== "localhost" ? window.location.origin : "";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CallAssistant = () => {
  const locale = useTolgee(["language"]).getLanguage();
  const viewer = useUserData().currentUser!;
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Stable origin used for postMessage validation — derived from MXCALL_URL alone.
  const mxCallOrigin = React.useMemo(() => new URL(MXCALL_URL).origin, []);

  // location.pathname is an intentional dep: back-button popstate updates React Router
  // location and reloads the iframe at the correct route. pushState (route-change handler)
  // bypasses React Router, so forward iframe navigation never triggers a recompute.
  const iframeSrc = React.useMemo(() => {
    const url = new URL(MXCALL_URL);
    const pathname = url.pathname
      .concat(location.pathname.replace("/callassistant", ""))
      .replace(/\/+/g, "/");
    url.pathname = pathname;

    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set("contractor_id", viewer.organisation.id);
    urlSearchParams.set("user_id", viewer.id);
    urlSearchParams.set("lang", locale.split("-").at(0)?.toLowerCase() ?? "de");
    const msysUrlValue = resolveMsysUrl();
    if (msysUrlValue) {
      urlSearchParams.set("msys_url", msysUrlValue);
    }
    const version = getVersion();
    if (version) {
      urlSearchParams.set("v", version);
    } else {
      urlSearchParams.delete("v");
    }
    url.search = urlSearchParams.toString();

    return url;
  }, [
    locale,
    location.pathname,
    location.search,
    viewer.organisation.id,
    viewer.id,
  ]);

  // Derive msysUrl the same way it's set in URL params
  const msysUrl = React.useMemo(() => resolveMsysUrl(), []);

  React.useEffect(() => {
    const handleMessage = async (event: MessageEvent<unknown>) => {
      if (event.origin !== mxCallOrigin) {
        return;
      }

      if (!isCallAssistantMessage(event.data)) {
        return;
      }

      const message = event.data;

      switch (message.type) {
        // ----- Route change (fire-and-forget) -----
        case "route-change": {
          // Reject paths that don't start with "/" (this also rejects empty string).
          if (!message.path.startsWith("/")) {
            break;
          }
          // Derive the mount prefix from the live browser URL so that any
          // VITE_ROUTER_BASENAME prefix is included, then resolve to catch
          // dot-segment traversal (e.g. "/../admin" → "/admin") and reject
          // anything that escapes the callassistant mount point.
          try {
            const currentPathname = window.location.pathname;
            const callassistantSegment = "/callassistant";
            const mountIndex = currentPathname.indexOf(callassistantSegment);
            const mountPrefix =
              mountIndex >= 0
                ? currentPathname.slice(
                    0,
                    mountIndex + callassistantSegment.length
                  )
                : callassistantSegment;
            const resolved = new URL(
              mountPrefix + message.path,
              window.location.origin
            );
            const { pathname } = resolved;
            if (
              pathname === mountPrefix ||
              pathname.startsWith(mountPrefix + "/")
            ) {
              window.history.pushState(null, "", pathname);
            }
          } catch {
            // invalid path — ignore
          }
          break;
        }

        // ----- Navigate (fire-and-forget) -----
        case "navigate": {
          const { replace = false } = message;
          let path: string | undefined;

          if ("path" in message) {
            path = message.path;
          } else if ("entity" in message) {
            switch (message.entity) {
              case "project":
                path = `/projects/${message.id}`;
                break;
              case "crmCompany":
                path = `/crm/organisations/${message.id}`;
                break;
              case "crmPerson":
                path = `/crm/organisations/${message.companyId}/users/${message.personId}`;
                break;
              case "quote":
                path = `/projects/${message.projectId}/quotes/${message.quoteId}`;
                break;
              case "requirement":
                path = `/projects/${message.projectId}/requirements/${message.requirementId}`;
                break;
            }
          }

          if (path) {
            navigate(path, { replace });
          }
          break;
        }

        // ----- Auth (request/reply via MessagePort) -----
        case "auth": {
          const port = event.ports[0];
          if (!port) return;

          switch (message.method) {
            case "requestAuthHeaders": {
              try {
                await auth.updateToken(5);
              } catch {
                // Token refresh failed — respond with empty headers
              }
              port.postMessage({
                headers: auth.token
                  ? { authorization: `Bearer ${auth.token}` }
                  : {},
              });
              port.close();
              break;
            }
          }
          break;
        }

        // ----- System (request/reply via MessagePort) -----
        case "system": {
          const port = event.ports[0];
          if (!port) return;

          switch (message.method) {
            case "getEnvironment": {
              const isNativePlatform = Capacitor.isNativePlatform();
              let device = null;
              let app = null;

              if (isNativePlatform) {
                try {
                  device = await Device.getInfo();
                  app = await CapacitorApp.getInfo();
                } catch {
                  // Native API calls failed — respond with nulls
                }
              }

              port.postMessage({
                isNativePlatform,
                device,
                app,
                msysUrl,
                organisationId: viewer.organisation.id,
                userId: viewer.id,
              });
              port.close();
              break;
            }

            case "getLatestVersion": {
              try {
                // MEIS-12358 Phase 8: read `version` from /config.json
                // instead of the separate /VERSION.json — single
                // runtime-config source.
                let version: string | undefined;
                if (Capacitor.isNativePlatform()) {
                  const appUrl =
                    (await getCapacitorAppUrl()) ??
                    "https://www.prod.meistersystems.de";
                  const response = await CapacitorHttp.get({
                    url: `${appUrl}/config.json`,
                  });
                  version = response.data.version;
                } else {
                  // `cache: "no-store"` mirrors the bootstrap fetch in
                  // `runtimeConfig.ts` so the parent app can't be served a
                  // stale cached `version` after a deploy.
                  const data = await (
                    await fetch("/config.json", { cache: "no-store" })
                  ).json();
                  version = data.version;
                }
                port.postMessage({ version });
              } catch (e) {
                port.postMessage({
                  version: null,
                  error:
                    e instanceof Error
                      ? e.message
                      : "Failed to fetch latest version",
                });
              }
              port.close();
              break;
            }
          }
          break;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    auth,
    msysUrl,
    mxCallOrigin,
    navigate,
    viewer.id,
    viewer.organisation.id,
  ]);

  return (
    <Page>
      <PageContainer $fullHeight $noPadding>
        <iframe
          style={{
            width: "100%",
            height: "100%",
            border: 0,
            padding: 0,
            margin: 0,
          }}
          src={iframeSrc.toString()}
          allow="microphone"
        ></iframe>
      </PageContainer>
    </Page>
  );
};
